/**
 * Mobile sticky Add to Cart bar.
 *
 * Does not implement its own add-to-cart logic. It mirrors the real,
 * existing product form's state and, on click, proxies to the real submit
 * button so Shopify's own AJAX handling (selling plans, variant id, cart
 * notification, error handling) runs unmodified.
 *
 * Its color swatches and quantity stepper are a second set of controls over
 * that SAME real form, not a parallel picker: a swatch click drives the
 * real page's own native color radio (so its full variant-switch flow runs
 * unmodified), and the quantity stepper reads/writes the real quantity
 * input directly. Neither duplicates Dawn's variant or quantity logic.
 */
(() => {
  function initStickyAtc(bar) {
    const sectionId = bar.dataset.sectionId;
    const targetButtonId = bar.dataset.targetButtonId;
    const realButton = document.getElementById(targetButtonId);
    const realPrice = document.getElementById(`price-${sectionId}`);
    // Real submit button and quantity input are mutated in place (attributes
    // toggled) by Dawn's own variant-change handling for a same-product
    // option change, never replaced with a new node — see
    // product-info.js's toggleSubmitButton/updateQuantityRules — so caching
    // these references once is safe. The color radio inputs are the one
    // piece Dawn DOES replace wholesale on every change (its variant-selects
    // viewTransition), so those are deliberately re-queried fresh below
    // instead of cached.
    const realQtyInput = document.getElementById(`Quantity-${sectionId}`);
    const stickyTitle = document.getElementById(`StickyAtcTitle-${sectionId}`);
    const shortSeriesName = bar.dataset.shortSeriesName || '';
    const stickyPrice = document.getElementById(`StickyAtcPrice-${sectionId}`);
    const stickyButton = document.getElementById(`StickyAtcButton-${sectionId}`);
    const stickyButtonText = stickyButton ? stickyButton.querySelector('.ift-sticky-atc__button-text') : null;
    const stickySpinner = stickyButton ? stickyButton.querySelector('.loading__spinner') : null;
    const stickySwatches = Array.from(bar.querySelectorAll('.ift-sticky-atc__swatch'));
    const stickyQtyInput = document.getElementById(`StickyAtcQty-${sectionId}`);
    const stickyQtyDecrease = bar.querySelector('[data-qty-decrease]');
    const stickyQtyIncrease = bar.querySelector('[data-qty-increase]');

    if (!realButton || !stickyButton) return;

    function clampQty(value, min, max, step) {
      let n = parseInt(value, 10);
      if (isNaN(n)) n = min;
      if (n < min) n = min;
      if (max != null && n > max) n = max;
      if (step > 1) {
        const offset = (n - min) % step;
        if (offset !== 0) n += step - offset;
        if (max != null && n > max) n = max;
      }
      return n;
    }

    function getRealQtyBounds() {
      if (!realQtyInput) return { min: 1, max: null, step: 1 };
      const min = parseInt(realQtyInput.min, 10) || 1;
      const max = realQtyInput.max ? parseInt(realQtyInput.max, 10) : null;
      const step = parseInt(realQtyInput.step, 10) || 1;
      return { min, max, step };
    }

    // The sticky stepper is a second control over the SAME real quantity
    // input the proxy-click ultimately submits — not a parallel state of
    // its own — so every change here is written straight through, and a
    // native 'change' event is dispatched so Dawn's own <quantity-input>
    // element (assets/global.js) runs its normal validation/UI updates too.
    function writeQtyToRealInput(value) {
      if (!realQtyInput) return;
      const previous = realQtyInput.value;
      realQtyInput.value = value;
      if (previous !== realQtyInput.value) {
        realQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function syncQtyFromReal() {
      if (!stickyQtyInput || !realQtyInput) return;
      const { min, max, step } = getRealQtyBounds();
      stickyQtyInput.min = min;
      if (max != null) {
        stickyQtyInput.max = max;
      } else {
        stickyQtyInput.removeAttribute('max');
      }
      stickyQtyInput.step = step;
      stickyQtyInput.value = clampQty(realQtyInput.value, min, max, step);
    }

    if (stickyQtyInput && realQtyInput) {
      // Covers a variant preselected via ?variant= in the URL, or any
      // quantity rule Liquid couldn't easily predict up front — same
      // rationale as the existing initial price/button sync below.
      syncQtyFromReal();

      function commitStickyQty() {
        const { min, max, step } = getRealQtyBounds();
        const clamped = clampQty(stickyQtyInput.value, min, max, step);
        stickyQtyInput.value = clamped;
        writeQtyToRealInput(clamped);
      }
      stickyQtyInput.addEventListener('change', commitStickyQty);
      stickyQtyInput.addEventListener('blur', commitStickyQty);
      if (stickyQtyDecrease) {
        stickyQtyDecrease.addEventListener('click', () => {
          const { min, max, step } = getRealQtyBounds();
          const clamped = clampQty(parseInt(stickyQtyInput.value, 10) - step, min, max, step);
          stickyQtyInput.value = clamped;
          writeQtyToRealInput(clamped);
        });
      }
      if (stickyQtyIncrease) {
        stickyQtyIncrease.addEventListener('click', () => {
          const { min, max, step } = getRealQtyBounds();
          const clamped = clampQty(parseInt(stickyQtyInput.value, 10) + step, min, max, step);
          stickyQtyInput.value = clamped;
          writeQtyToRealInput(clamped);
        });
      }
    }

    function isMobileViewport() {
      // Matches the sticky bar's own CSS breakpoint (assets/sticky-add-to-cart.css:
      // "min-width: 750px" hides it) — this scroll-to-top behavior is a
      // mobile-only nicety, same as the bar itself, and must not touch
      // desktop.
      return window.matchMedia('(max-width: 749px)').matches;
    }

    function scrollToPageTop() {
      // The customer's own ask was "all the way to the top" — scrolling the
      // product wrapper into view instead left the announcement bar/header
      // above it out of frame, which read as not-quite-the-top. A plain
      // window scroll-to-0 has no such ambiguity.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function selectStickySwatch(swatch) {
      if (!swatch || swatch.disabled) return;
      stickySwatches.forEach((s) => {
        s.classList.remove('is-selected');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-selected');
      swatch.setAttribute('aria-pressed', 'true');

      const priceFormatted = swatch.dataset.variantPriceFormatted;
      if (stickyPrice && priceFormatted) stickyPrice.innerHTML = priceFormatted;

      // Only the short-name products swap their title text per variant —
      // the fallback title (a product this bar has no short name for) is
      // just the real product title, which doesn't change with color.
      if (stickyTitle && shortSeriesName && swatch.dataset.variantTitle) {
        stickyTitle.textContent = `${shortSeriesName} - ${swatch.dataset.variantTitle}`;
      }

      // The real color radio is replaced with a fresh node by Dawn's own
      // variant-selects viewTransition on every change, so it's queried
      // fresh here rather than cached — reused as-is (not duplicated) so
      // the real page's full variant-switch flow (media, price, URL, form)
      // runs exactly as it would from a real click.
      const optionValueId = swatch.dataset.optionValueId;
      const realInput = document.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (realInput && !realInput.checked) {
        realInput.checked = true;
        realInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    stickySwatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        if (swatch.classList.contains('is-selected')) return;
        selectStickySwatch(swatch);
      });
    });

    // "Disabled" (variant unavailable / sold out) shows the label text in a
    // dimmed state. "Submitting" (a click is in flight) additionally swaps
    // in the spinner and hides the label. Keeping these separate avoids
    // showing a spinner for a simple sold-out variant.
    function setDisabled(isDisabled) {
      if (isDisabled) {
        stickyButton.setAttribute('aria-disabled', 'true');
      } else {
        stickyButton.removeAttribute('aria-disabled');
      }
    }

    function setSubmitting(isSubmitting) {
      setDisabled(isSubmitting);
      if (stickySpinner) stickySpinner.classList.toggle('hidden', !isSubmitting);
      if (stickyButtonText) stickyButtonText.classList.toggle('hidden', isSubmitting);
    }

    let controlsVisible = true;

    function isPrivacyBannerShowing() {
      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return false;
      const rect = banner.getBoundingClientRect();
      return rect.height > 0 && getComputedStyle(banner).display !== 'none';
    }

    function updateVisibility() {
      const shouldShow = !controlsVisible && !isPrivacyBannerShowing();
      if (shouldShow) {
        bar.hidden = false;
        // Allow the transform transition to run after unhiding.
        requestAnimationFrame(() => bar.setAttribute('data-visible', 'true'));
      } else {
        bar.removeAttribute('data-visible');
        bar.hidden = true;
      }
    }

    // Hide the sticky bar for as long as the real purchase controls are
    // visible; show it once they've scrolled out of view.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          controlsVisible = entry.isIntersecting;
        });
        updateVisibility();
      },
      { threshold: 0 }
    );
    observer.observe(realButton);

    // Re-check periodically on scroll too, in case a privacy banner
    // appears/disappears without an intersection change.
    let bannerCheckTimeout;
    window.addEventListener(
      'scroll',
      () => {
        clearTimeout(bannerCheckTimeout);
        bannerCheckTimeout = setTimeout(updateVisibility, 100);
      },
      { passive: true }
    );

    function syncFromRealButton() {
      const disabled = realButton.getAttribute('aria-disabled') === 'true' || realButton.disabled;
      setDisabled(disabled);
      const realLabel = realButton.querySelector('span');
      if (realLabel && stickyButtonText) {
        stickyButtonText.textContent = realLabel.textContent.trim();
      }
    }

    function syncPrice() {
      if (realPrice && stickyPrice) {
        stickyPrice.innerHTML = realPrice.innerHTML;
      }
    }

    // Initial sync covers any state Liquid couldn't easily predict up front
    // (quantity rules, gift-card-only variants, a variant preselected via
    // ?variant= in the URL, etc.) without duplicating that logic here.
    syncFromRealButton();
    syncPrice();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        // Dawn updates the real button/price synchronously before this
        // event fires, so reading them here reflects the new variant.
        syncFromRealButton();
        syncPrice();
        syncQtyFromReal();

        // Reconcile which swatch is actually selected against the real
        // variant Dawn resolved (rather than trusting our own optimistic
        // guess from the click) — covers a click on an option combination
        // Dawn rejects/adjusts, and keeps the sticky bar correct if the
        // color is ever changed from the real picker up top while this bar
        // is visible.
        const newVariant = event?.data?.variant;
        if (newVariant?.id != null) {
          stickySwatches.forEach((s) => {
            const isMatch = String(s.dataset.variantId) === String(newVariant.id);
            s.classList.toggle('is-selected', isMatch);
            s.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          });
          if (stickyTitle && shortSeriesName && newVariant.title) {
            stickyTitle.textContent = `${shortSeriesName} - ${newVariant.title}`;
          }
        }

        // Runs for a color change made from the sticky bar's own swatches
        // OR the real picker up top — either way Dawn's own focus-the-
        // changed-input call (chained as the next .then() after the same
        // update that publishes this event, i.e. it hasn't run yet at this
        // exact point) is about to auto-scroll to wherever that input
        // landed. A macrotask always runs after any already-queued
        // microtask, so deferring even to the next tick is enough to let
        // Dawn's scroll fire first and have ours override it, landing at
        // the top instead of stranded at the picker. Mobile-only, matching
        // the sticky bar itself — desktop is untouched.
        if (isMobileViewport()) {
          setTimeout(scrollToPageTop, 50);
        }
      });

      // Once WE'VE initiated a submission, our own submitting flag is the
      // source of truth for re-enabling — re-checking the real button's
      // current aria-disabled here would race against Dawn clearing it
      // (which can happen slightly after cart-update/cart-error fire) and
      // could re-disable us right after we just cleared it.
      let submitting = false;
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (!submitting) return;
        submitting = false;
        setSubmitting(false);
      });
      subscribe(PUB_SUB_EVENTS.cartError, () => {
        if (!submitting) return;
        submitting = false;
        setSubmitting(false);
      });

      stickyButton.addEventListener('click', () => {
        if (submitting || stickyButton.getAttribute('aria-disabled') === 'true') return;
        submitting = true;
        setSubmitting(true);
        realButton.click();
        // Safety fallback in case cart-update/cart-error never fires for
        // some reason, so the button doesn't stay stuck disabled forever.
        setTimeout(() => {
          if (submitting) {
            submitting = false;
            setSubmitting(false);
          }
        }, 8000);
      });
    } else {
      // Pub/sub not available for some reason — still make the button work,
      // just without the richer state syncing.
      stickyButton.addEventListener('click', () => {
        if (stickyButton.getAttribute('aria-disabled') === 'true') return;
        realButton.click();
      });
    }
  }

  function init() {
    document.querySelectorAll('.ift-sticky-atc').forEach(initStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
